import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Brain, Moon, Sun, History, Home, Settings, ChevronLeft, Rocket, X } from 'lucide-react';
import { useDarkMode } from '../hooks/useDarkMode';

interface SidebarProps {
  isCollapsed: boolean;
  isMobile?: boolean;
  isOpen?: boolean;
  onToggle: () => void;
  onMobileClose?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  isCollapsed,
  isMobile = false,
  isOpen = false,
  onToggle,
  onMobileClose
}) => {
  const { darkMode, toggleDarkMode } = useDarkMode();
  const location = useLocation();

  const menuItems = [
    {
      path: '/',
      icon: Home,
      title: 'Nowa analiza',
      label: 'Analiza'
    },
    {
      path: '/history',
      icon: History,
      title: 'Historia analiz',
      label: 'Historia'
    },
    {
      path: '/settings',
      icon: Settings,
      title: 'Ustawienia organizacji',
      label: 'Ustawienia'
    },
    {
      path: '/implementation',
      icon: Rocket,
      title: 'Wdróż CallOS w swojej firmie',
      label: 'Wdróż u siebie'
    }
  ];

  // For mobile, sidebar should be hidden by default and shown as overlay
  const sidebarClasses = `
    fixed top-0 left-0 h-full bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl border-r border-gray-200/50 dark:border-gray-700/50
    transition-all duration-300 ease-in-out z-50
    ${isMobile
      ? `${isOpen ? 'translate-x-0' : '-translate-x-full'} w-64`
      : `${isCollapsed ? 'w-20' : 'w-64'}`
    }
  `;

  return (
    <div className={sidebarClasses}>
      {/* Header */}
      <div className="p-4 border-b border-gray-200/50 dark:border-gray-700/50">
        <div className="flex items-center justify-between">
          {(!isCollapsed || isMobile) && (
            <div className="flex items-center space-x-3">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl blur-lg opacity-30 animate-pulse-glow"></div>
                <div className="relative bg-gradient-to-r from-primary-500 to-secondary-500 p-2 rounded-xl">
                  <Brain className="w-6 h-6 text-white" />
                </div>
              </div>
              <div>
                <h1 className="text-xl font-bold gradient-text">CallOS</h1>
                <p className="text-xs text-gray-600 dark:text-gray-300">
                  AI Sales Analyzer
                </p>
              </div>
            </div>
          )}
          
          {isCollapsed && !isMobile && (
            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl blur-lg opacity-30 animate-pulse-glow"></div>
                <div className="relative bg-gradient-to-r from-primary-500 to-secondary-500 p-2 rounded-xl">
                  <Brain className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          )}
          
          {isMobile ? (
            <button
              onClick={onMobileClose}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
              title="Zamknij menu"
            >
              <X className="w-6 h-6 text-gray-600 dark:text-gray-300" />
            </button>
          ) : (
            <button
              onClick={onToggle}
              className={`p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200
                ${isCollapsed ? 'rotate-180' : ''}
              `}
              title={isCollapsed ? 'Rozwiń sidebar' : 'Zwiń sidebar'}
            >
              <ChevronLeft className="w-6 h-6 text-gray-600 dark:text-gray-300" />
            </button>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="p-4 space-y-2">
        {menuItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={isMobile ? onMobileClose : undefined}
              className={`
                flex items-center rounded-xl transition-all duration-200
                ${isActive
                  ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 shadow-sm'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                }
                ${(isCollapsed && !isMobile) ? 'justify-center p-3' : 'space-x-3 p-3'}
              `}
              title={isCollapsed && !isMobile ? item.title : ''}
            >
              <Icon className={`w-6 h-6 ${isActive ? 'text-primary-600 dark:text-primary-400' : ''}`} />
              {(!isCollapsed || isMobile) && (
                <span className="font-medium">{item.label}</span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Dark Mode Toggle */}
      <div className="absolute bottom-4 left-4 right-4">
        <button
          onClick={toggleDarkMode}
          className={`
            w-full flex items-center rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800
            transition-all duration-200 text-gray-700 dark:text-gray-300
            ${(isCollapsed && !isMobile) ? 'justify-center p-3' : 'space-x-3 p-3'}
          `}
          title={isCollapsed && !isMobile ? (darkMode ? 'Przełącz na tryb jasny' : 'Przełącz na tryb ciemny') : ''}
        >
          {darkMode ? (
            <Sun className="w-6 h-6 text-accent-500" />
          ) : (
            <Moon className="w-6 h-6 text-primary-600" />
          )}
          {(!isCollapsed || isMobile) && (
            <span className="font-medium">
              {darkMode ? 'Tryb jasny' : 'Tryb ciemny'}
            </span>
          )}
        </button>
      </div>
    </div>
  );
};